<!-- 巡检路线新增/编辑弹窗 -->
<template>
  <div>
    <dialog-reset
      top="1vh"
      width="80%"
      height="80vh"
      :title="isEdit ? '编辑巡检路线' : '新增巡检路线'"
      :visible.sync="dialogVisible"
      :appendToBody="false"
      :modalShow="false"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="120px" v-loading="loading">
        <!-- 基本信息 -->
        <div class="form-section">
          <div class="section-title">
            <span class="required">基本信息*</span>
          </div>

          <el-form-item label="路线名称" prop="routeName">
            <el-input
              v-model="form.routeName"
              placeholder="请输入路线名称"
              maxlength="50"
              show-word-limit
              style="width: 100%"
            />
          </el-form-item>

          <el-form-item label="路线描述" prop="remark">
            <el-input
              v-model="form.remark"
              type="textarea"
              placeholder="请输入路线描述（可选）"
              :rows="3"
              maxlength="200"
              show-word-limit
              style="width: 100%"
            />
          </el-form-item>
        </div>

        <!-- 路线点位序列（独立 class：避免 scoped 样式经 dialog 插槽后未命中） -->
        <div class="form-section form-section--route-points">
          <div class="inspection-route-points-ui route-dialog-inspection-route-points">
            <div class="section-title section-title--with-badge">
              <span class="section-title__text">路线点位序列</span>
              <span class="section-title__count">{{ pointList.length }}</span>
            </div>
            <div class="route-point-panel">
              <div class="route-point-panel__toolbar">
                <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAddPoint">
                  添加点位
                </el-button>
              </div>

              <div v-if="pointList.length === 0" class="empty-points">
                <i class="el-icon-location-outline empty-points__icon" />
                <p class="empty-points__text">暂无点位，请先点击「添加点位」从列表中选择</p>
              </div>

              <div v-else class="route-point-sequence">
                <div
                  v-for="(point, index) in pointList"
                  :key="point.pointCode || `pt-${index}`"
                  class="route-point-block"
                >
                  <div class="route-point-block__shell">
                    <div class="route-point-headline">
                      <span class="route-point-headline__badge" aria-hidden="true">
                        <i class="el-icon-location-outline" />
                      </span>
                      <div class="route-point-headline__content">
                        <div class="route-point-headline__meta-row">
                          <span class="route-point-headline__order">{{ chineseOrderNumeral(index + 1) }}</span>
                          <span class="route-point-headline__prefix">、巡检点位：</span>
                          <span class="route-point-headline__name-highlight">{{ point.pointName || "—" }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="route-point-card">
                      <div class="route-point-card__main">
                        <div class="route-point-card__items-block">
                          <div class="route-point-card__items-heading">巡检项</div>
                          <div
                            v-if="getPointInspectionItemNames(point).length"
                            class="route-point-card__item-list"
                            role="list"
                          >
                            <div
                              v-for="(itemName, itemIdx) in getPointInspectionItemNames(point)"
                              :key="itemIdx"
                              class="route-point-card__item-row"
                              role="listitem"
                            >
                              <span class="route-point-card__item-idx">{{ itemIdx + 1 }}、</span>
                              <span class="route-point-card__item-name">{{ itemName }}</span>
                            </div>
                          </div>
                          <div v-else class="route-point-card__items-empty">—</div>
                        </div>
                      </div>
                      <div class="route-point-card__actions">
                        <el-button
                          type="text"
                          size="small"
                          class="route-point-card__btn-remove"
                          icon="el-icon-delete"
                          @click="handleRemovePoint(index)"
                        >
                          删除
                        </el-button>
                        <div class="route-point-card__move-btns">
                          <el-button
                            type="text"
                            size="small"
                            icon="el-icon-top"
                            :disabled="index === 0"
                            @click="handleMoveUp(index)"
                          >
                            上移
                          </el-button>
                          <el-button
                            type="text"
                            size="small"
                            icon="el-icon-bottom"
                            :disabled="index === pointList.length - 1"
                            @click="handleMoveDown(index)"
                          >
                            下移
                          </el-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 路线属性 -->
        <div class="form-section">
          <div class="section-title">
            <span>路线属性</span>
          </div>

          <el-form-item label="预计耗时" prop="estimatedTime">
            <el-input-number
              v-model="form.estimatedTime"
              :min="0"
              :max="10000"
              :precision="0"
              controls-position="right"
              style="width: 200px"
            />
            <span style="margin-left: 10px; color: #909399">分钟</span>
          </el-form-item>
        </div>
      </el-form>

      <div slot="dialog_footer" class="dialog-footer">
        <el-button @click="handleCancel">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">
          {{ isEdit ? "更新" : "保存" }}
        </el-button>
      </div>
    </dialog-reset>


    <!-- 选择点位弹窗 -->
    <point-dialog
      :visible.sync="pointDialogVisible"
      :selected-points="pointList.map(p => p.pointCode)"
      @confirm="handleSelectPoint"
    />
  </div>

</template>

<script>
import { add, update, getDetail } from "../api";
import PointDialog from "./PointDialog.vue";
import validator from "@/util/validate.js";

export default {
  name: "RouteDialog",
  components: {
    PointDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    formData: {
      type: Object,
      default: () => ({}),
    },
    isCopy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      submitLoading: false,
      pointDialogVisible: false,
      pointList: [],
      form: {
        routeCode: "",
        routeName: "",
        remark: "",
        pointCount: 0,
        estimatedTime: 0,
        totalDistance: 0,
        status: 1,
        inspectionRouteExtRpcDto: [],
      },
      rules: {
        routeName: [
          { required: true, message: "请输入路线名称", trigger: "blur" },
          {
            min: 2,
            max: 50,
            message: "长度在 2 到 50 个字符",
            trigger: "blur",
          },
        ],
        estimatedTime: [
          { required: true, message: "请输入预计耗时", trigger: "blur" },
          {
            validator:validator.positiveThanZero,
          },
        ],
      },
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initForm();
      } else {
        this.pointDialogVisible = false;
        this.resetForm();
      }
    },
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        if (!val) {
          this.pointDialogVisible = false;
        }
        this.$emit("update:visible", val);
      },
    },
  },
  methods: {
    /** 路线序号中文：一、二、三…（用于「一、巡检点位：xxx」） */
    chineseOrderNumeral(num) {
      const n = Number(num);
      if (!n || n < 1) return "一";
      const d = "零一二三四五六七八九";
      if (n <= 10) return n === 10 ? "十" : d[n];
      if (n < 20) return "十" + (n % 10 ? d[n % 10] : "");
      if (n < 100) {
        const tens = Math.floor(n / 10);
        const ones = n % 10;
        return d[tens] + "十" + (ones ? d[ones] : "");
      }
      return String(n);
    },
    /**
     * 巡检项名称列表（有序）：优先子表多行，按 sequenceNumber；否则按主表逗号/顿号拆分
     */
    getPointInspectionItemNames(point) {
      if (!point) return [];
      const list = point.inspectionPointExtRpcDtoList;
      if (Array.isArray(list) && list.length) {
        const sorted = [...list].sort((a, b) => {
          const sa = a && a.sequenceNumber != null ? Number(a.sequenceNumber) : 0;
          const sb = b && b.sequenceNumber != null ? Number(b.sequenceNumber) : 0;
          if (sa !== sb) return sa - sb;
          const ia = a && a.id != null ? Number(a.id) : 0;
          const ib = b && b.id != null ? Number(b.id) : 0;
          return ia - ib;
        });
        return sorted.map((x) => x && x.associatedObjectName).filter(Boolean);
      }
      if (point.associatedObjectName) {
        return String(point.associatedObjectName)
          .split(/[,，、]/)
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [];
    },
    /** 从 axios 响应中取出详情 DTO（兼容 R.data 包裹） */
    extractDetailPayload(response) {
      if (!response || !response.data) {
        return null;
      }
      const body = response.data;
      if (body.data !== undefined && body.data !== null) {
        return body.data;
      }
      if (body && typeof body === "object" && !Array.isArray(body) && body.id != null) {
        return body;
      }
      return null;
    },

    /** 详情/列表中的路线子表（点位序列）统一解析为数组 */
    normalizePointListFromDetail(data) {
      if (!data || typeof data !== "object") {
        return [];
      }
      const ext =
        data.inspectionRouteExtRpcDto ||
        data.inspectionRouteExtRpcDtoList ||
        [];
      if (!Array.isArray(ext)) {
        return [];
      }
      return [...ext].sort(
        (a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0)
      );
    },

    // 初始化表单
    async initForm() {
      if (this.isEdit && this.formData.id) {
        this.loading = true;
        try {
          const response = await getDetail(this.formData.id);
          const payload = this.extractDetailPayload(response);
          if (payload && typeof payload === "object") {
            this.fillFormData(payload);
          } else {
            this.fillFormData(this.formData);
          }
        } catch (error) {
          console.error("加载数据失败:", error);
          this.fillFormData(this.formData);
        } finally {
          this.loading = false;
        }
      } else if (this.isCopy && this.formData.id) {
        this.loading = true;
        try {
          const response = await getDetail(this.formData.id);
          const payload = this.extractDetailPayload(response);
          if (payload && typeof payload === "object") {
            this.fillCopyFormData(payload);
          }
        } catch (error) {
          console.error("加载数据失败:", error);
        } finally {
          this.loading = false;
        }
      } else {
        this.resetForm();
      }
    },

    // 填充表单数据（编辑 / 查看转编同一套 UI）
    fillFormData(data) {
      const d = data && typeof data === "object" ? { ...data } : {};
      this.form = d;
      this.pointList = this.normalizePointListFromDetail(d).map((row) => ({ ...row }));
      this.updateFormData();
    },

    // 填充复制表单数据
    fillCopyFormData(dto) {
      const data = dto && typeof dto === "object" ? { ...dto } : {};
      const baseName = data.routeName || "";
      this.fillFormData(data);
      this.form = {
        ...this.form,
        routeCode: "",
        routeName: baseName ? `${baseName} - 副本` : "副本",
        status: 1,
      };
      this.updateFormData();
    },

    // 重置表单
    resetForm() {
      this.form = {
        routeCode: "",
        routeName: "",
        remark: "",
        pointCount: 0,
        estimatedTime: 0,
        totalDistance: 0,
        status: 1,
        inspectionRouteExtRpcDto: [],
      };
      this.pointList = [];
    },

    // 添加点位
    handleAddPoint() {
      if (this.pointDialogVisible) {
        this.pointDialogVisible = false;
        this.$nextTick(() => {
          this.pointDialogVisible = true;
        });
        return;
      }
      this.pointDialogVisible = true;
    },

    /**
     * 选择点位：以弹窗当前勾选为准。
     * - 取消勾选的点会从路线序列中移除；
     * - 仍勾选的点保持原有顺序；
     * - 新勾选的点按其在弹窗勾选结果中的顺序接在后面。
     */
    handleSelectPoint(points) {
      const incoming = Array.isArray(points) ? points : [];
      const selectedCodes = new Set(
        incoming.map((p) => p && p.pointCode).filter(Boolean)
      );

      const keptInOrder = this.pointList
        .filter((p) => p && p.pointCode && selectedCodes.has(p.pointCode))
        .map((p) => ({ ...p }));

      const oldCodes = new Set(
        this.pointList.map((p) => p && p.pointCode).filter(Boolean)
      );
      const added = [];
      const seenNew = new Set();
      for (const p of incoming) {
        if (!p || !p.pointCode) {
          continue;
        }
        const code = p.pointCode;
        if (oldCodes.has(code)) {
          continue;
        }
        if (seenNew.has(code)) {
          continue;
        }
        seenNew.add(code);
        added.push({ ...p });
      }

      this.pointList = [...keptInOrder, ...added];
      this.updateFormData();
    },

    // 删除点位
    handleRemovePoint(index) {
      this.pointList.splice(index, 1);
      this.updateFormData();
    },

    // 上移
    handleMoveUp(index) {
      if (index > 0) {
        const temp = this.pointList[index];
        this.$set(this.pointList, index, this.pointList[index - 1]);
        this.$set(this.pointList, index - 1, temp);
        this.updateFormData();
      }
    },

    // 下移
    handleMoveDown(index) {
      if (index < this.pointList.length - 1) {
        const temp = this.pointList[index];
        this.$set(this.pointList, index, this.pointList[index + 1]);
        this.$set(this.pointList, index + 1, temp);
        this.updateFormData();
      }
    },

    // 更新表单数据
    updateFormData() {
      this.form.pointCount = this.pointList.length;
      this.form.inspectionRouteExtRpcDto = this.pointList.map((point, index) => ({
        ...point,
        sequenceNumber: index + 1,
        routeName: this.form.routeName,
        extId: this.form.routeCode,
      }));
    },

    // 取消
    handleCancel() {
      this.dialogVisible = false;
    },

    // 关闭
    handleClose() {
      this.resetForm();
    },

    // 提交
    handleSubmit() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          if (this.pointList.length === 0) {
            this.$message.warning("请至少添加一个点位");
            return;
          }

          this.submitLoading = true;
          try {
            this.updateFormData();
            if (this.isEdit) {
              await update(this.form);
              this.$message.success("更新巡检路线成功");
            } else {
              await add(this.form);
              this.$message.success("新增巡检路线成功");
            }
            this.dialogVisible = false;
            this.$emit("refresh");
          } catch (error) {
            console.error("提交失败:", error);
            const fallback = this.isEdit ? "更新失败" : "新增失败";
            const serverMsg =
              (error &&
                error.message &&
                String(error.message).trim()) ||
              (error &&
                error.response &&
                error.response.data &&
                (error.response.data.msg || error.response.data.message));
            this.$message.error(serverMsg || fallback);
          } finally {
            this.submitLoading = false;
          }
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.form-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e4e7ed;

  &:last-of-type {
    border-bottom: none;
  }

  .section-title {
    margin-bottom: 15px;
    font-size: 14px;
    font-weight: 600;
    color: #303133;

    .required {
      color: #f56c6c;
    }
  }

}

.dialog-footer {
  text-align: right;

  .el-button {
    min-width: 80px;
  }
}
</style>

<style lang="scss">
@import "./route-dialog-points.scss";
</style>
